/**
 * UIBloom — the shared-WebGL bloom for the HUD (BL-37b / S87, ART §9 + §6.10).
 *
 * This is the "MAX UI bar" promotion: the S86 engine-material UI-chrome canvas
 * (`UIChromeCanvas` → real `emissiveFill`/`glowCache`) is run through a SECOND
 * instance of the arena's `WebGL2Effects` bloom pipeline — the SAME class, the
 * SAME GLSL — so the HUD lights with the exact material as the battlefield.
 * Only the *uniform values* differ, calibrated for the HUD (`UI_BLOOM` tokens):
 * a higher threshold (only hot edges bloom, not the whole panel), a tighter
 * full-res blur (a defined filament, not a foggy halo), chroma off (no fringe
 * near text). No new fragile shader; the arena pass is byte-untouched (separate
 * instance over a separate output canvas).
 *
 * Architecture (mirrors the arena exactly):
 *   - the arena's hidden `#webgl-canvas` is the WebGL output, drawn back over
 *     the visible `#game-canvas`; here the hidden output canvas is the WebGL
 *     target, drawn back over the visible `#ui-chrome-canvas` (in
 *     `UIChromeCanvas.applyBloom`).
 *   - `process()` is alpha-aware (bloom-only contribution, luminance alpha), so
 *     transparent chrome regions stay transparent over the DOM.
 *
 * Performance (§6.10): the HUD is static most frames, so this runs only when
 * the chrome is dirty (`UIChromeCanvas` calls `process()` from `present()`,
 * which fires on relayout / register / resize / an active value-flash). Idle
 * cost ≈ 0. The working resolution is capped to CSS-logical (÷ DPR) so a HiDPI
 * HUD never pays DPR² fill on a dirty frame.
 *
 * Degrade (BL-44 — the UI bloom drops FIRST): `setEnabled(false)` /
 * context-loss / quality `off|low` → `process()` returns `null` and the raw S86
 * chrome (still lit by `glowCache`) shows through. The seam is intentionally
 * reusable for the eventual BL-44 drop-priority unification (SYSTEMS_MAP §4).
 */
import { WebGL2Effects } from "../rendering/WebGL2Effects.ts";
import type { QualityLevel } from "../core/types.ts";
import type { UIChromePostFx } from "./UIChromeCanvas.ts";
import { UI_BLOOM } from "./uiTokens.ts";

export class UIBloom implements UIChromePostFx {
  private readonly effects: WebGL2Effects;
  private readonly output: HTMLCanvasElement;
  private readonly ownsOutput: boolean;
  private enabled = true;
  private dpr = 1;

  /**
   * @param output optional output canvas (tests inject one). In the live app a
   *   detached canvas is created — it's a WebGL render target + `drawImage`
   *   source only, never in the DOM (the visible layer is `#ui-chrome-canvas`),
   *   so it needs no layout/z-index and mirrors the arena's hidden target.
   */
  constructor(output?: HTMLCanvasElement) {
    this.ownsOutput = output === undefined;
    this.output =
      output ??
      (typeof document !== "undefined"
        ? document.createElement("canvas")
        : ({} as HTMLCanvasElement));
    this.effects = new WebGL2Effects(this.output, {
      threshold: UI_BLOOM.threshold,
      intensityHigh: UI_BLOOM.intensityHigh,
      intensityMedium: UI_BLOOM.intensityMedium,
      chromaOffset: UI_BLOOM.chromaOffset,
      bloomDownscale: UI_BLOOM.bloomDownscale,
      // Cap the UI bloom's working resolution to CSS-logical pixels (÷ DPR),
      // mirroring the arena's POST_FX_RESOLUTION_CAP intent: a soft glow over a
      // HiDPI HUD must not pay DPR² fill — especially on a dirty-redraw frame.
      resolveResolution: (w, h) => ({
        w: Math.max(1, Math.round(w / this.dpr)),
        h: Math.max(1, Math.round(h / this.dpr)),
      }),
    });
  }

  /** {@link UIChromePostFx} — bloom the chrome, or `null` to show raw chrome. */
  process(source: HTMLCanvasElement): HTMLCanvasElement | null {
    if (!this.enabled) return null;
    if (source.width <= 0 || source.height <= 0) return null;
    // BL-44 fallback: no live WebGL context, or the quality ladder has dropped
    // to a no-post tier → raw S86 chrome (still lit) shows through.
    if (this.effects.isContextLost()) return null;
    const q = this.effects.getQuality();
    if (q === "off" || q === "low") return null;
    this.effects.render(source);
    // Re-check: the context can be lost mid-render on some GPUs.
    if (this.effects.isContextLost()) return null;
    return this.effects.getCanvas();
  }

  /** {@link UIChromePostFx} — the source DPR drives the resolution cap. */
  setDpr(dpr: number): void {
    this.dpr = Math.max(1, dpr || 1);
  }

  /** BL-44 drop-first hook: toggle the UI bloom off → raw chrome fallback. */
  setEnabled(on: boolean): void {
    this.enabled = on;
    this.effects.enabled = on;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  /** Independently quality-gate the UI bloom (it does NOT share the arena's
   *  frame-time budget accounting — it's dirty-redraw, not per-frame). */
  setQuality(quality: QualityLevel): void {
    this.effects.setQuality(quality);
  }

  getQuality(): QualityLevel {
    return this.effects.getQuality();
  }

  /** Test hook — the underlying effects instance (for the arena-isolation lock). */
  getEffects(): WebGL2Effects {
    return this.effects;
  }

  destroy(): void {
    this.effects.destroy();
    if (this.ownsOutput) this.output.remove?.();
  }
}
